import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./UploadDocuments.module.scss";
import {
  UnderwritingFormContext,
  bankStatementFileInitialState,
  bankStatementsInitialState,
  getInitialBankStatementDetails,
} from "../context/UnderwritingFormContext";
import { Form, FormSpy } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import BankStatements from "./BankStatements";
import {
  findFirstErrorField,
  setFieldTouchedMutator,
} from "../../../components/functions/utilities";
import MeritoButton from "../../../components/common/MeritoButtons/MeritoButtons";
import { RiAddFill as AddIcon } from "react-icons/ri";
import {
  StatementAnalysisStatus,
  StatementStepType,
  UnderwritingSteps,
  uploadStates,
} from "../../../interfaces/interfaces";
import API from "../../../rest/api";
import { useBlocker } from "react-router";
import { getNextStepperLink } from "../UnderwritingSections/UnderwritingSections";
import { getIn } from "final-form";
import { useSearchParams } from "react-router-dom";

type Props = {
  setFormPristine: (value: boolean) => void;
  saveToLocalStorage?: (values: Record<string, any>) => void;
};

export const bankStatementValidation = (
  values,
  underwritingFileJobs,
  statementJobs
) => {
  // console.log('values', values);
  let errors = {};
  const bankStatements = values.bankStatements;
  const rootError = [];
  const statementError = [];
  const passwordError = [];
  const failureStates = new Set([
    StatementAnalysisStatus.failure,
    StatementAnalysisStatus.requestTimedOut,
  ]);
  if (!bankStatements || !bankStatements.length) {
    errors["rootBankError"] = "Please add atleast one bank account";
    rootError.push("Please add atleast one bank account");
  } else {
    const allBankStatementJobs = getIn(
      underwritingFileJobs,
      `${UnderwritingSteps.documents}`
    );
    bankStatements.forEach((bankStatement, index) => {
      const statements = bankStatement.statements;
      const statementType = bankStatement.statementType;
      if (
        !statements ||
        (!statements.length &&
          bankStatement?.statementType == StatementStepType.pdf_upload)
      ) {
        errors[`bankStatements[${index}].statements`] =
          "Please add atleast one statement";
        rootError.push("Please add atleast one statement");
      } else {
        const statementJob = bankStatement.statementJobId;
        const currentJob = getIn(
          statementJobs,
          `${statementJob}.${statementType}`
        );
        // console.log('currentJob', currentJob);

        if (currentJob && failureStates.has(currentJob.status)) {
          errors[`bankStatements[${index}].statementJobId`] =
            "Statement processing failed";
          statementError.push("Statement processing failed");
        }
        statements?.forEach((individualStatement, innerIndex) => {
          const uploadedFileJob =
            allBankStatementJobs[
              `bankStatements[${index}].statements[${innerIndex}]`
            ];
          // console.log('uploadedFileJob', uploadedFileJob)
          if (uploadedFileJob?.status == uploadStates?.requiredPassword) {
            errors[`bankStatements[${index}].statements[${innerIndex}]`] =
              "Please enter the password";
            passwordError.push("Please enter the password");
          }
        });
      }
    });
  }

  return {
    errors: errors,
    allErrors: {
      rootError,
      statementError,
      passwordError,
    },
  };
};

const UploadDocuments = forwardRef(({ setFormPristine, saveToLocalStorage }: Props, ref) => {
  const {
    formData,
    setFormData,
    setStepValidity,
    formStepsValidity,
    statementAnalysisJobs,
    underwritingFileJobs,
  } = useContext(UnderwritingFormContext);
  const formRef = useRef(null);
  const [deleteFileKeys, setDeleteFileKeys] = useState([]);
  const [newUploadedDocIds, setNewUploadedDocIds] = useState<number[]>([]);
  const [deleteDocIds, setDeleteDocIds] = useState<number[]>([]);

  // let blocker = useBlocker(
  //     ({ currentLocation, nextLocation }) =>
  //         currentLocation.pathname !== nextLocation.pathname && currentLocation?.pathname != getNextStepperLink(UnderwritingSteps.documents),
  // );

  // const dialogRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // console.log('params', params);
    const showError = searchParams.get("error");
    // console.log('showError', showError);
    if (showError == "true") {
      scrollToFirstError();
    }
  }, [searchParams]);

  useImperativeHandle(
    ref,
    () => ({
      submitForm: () => {
        return new Promise((resolve) => {
          formRef.current.submit();
          console.log("pristing", formRef?.current?.getState());
          if (!formStepsValidity[UnderwritingSteps.documents]) {
            scrollToFirstError();
            resolve({
              success: false,
              values: formRef?.current?.getState().values,
              pristine: formRef?.current?.getState().pristine,
              deleteDocIds,
              newDocIds: newUploadedDocIds,
            });
          } else {
            // deleteFileKeysOnSubmit();
            resolve({
              success: true,
              values: formRef?.current?.getState().values,
              pristine: formRef?.current?.getState().pristine,
              deleteDocIds,
              newDocIds: newUploadedDocIds,
            });
          }
        });
      },
    }),
    [formStepsValidity, deleteDocIds, newUploadedDocIds]
  );

  const deleteFileKeysOnSubmit = () => {
    if (deleteFileKeys && deleteFileKeys.length) {
      for (const fileKey of deleteFileKeys) {
        try {
          API.post("/app/DeleteDoc", { key: fileKey }).then((res) => res.data);
        } catch (error) {
          console.log("error", error);
        }
      }
    }
  };

  const onAddAccountScroll = (divId: string) => {
    const element = document.getElementById(divId);
    element?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToFirstError = () => {
    try {
      const formState = formRef.current.getState();
      const { errors } = formState;

      // console.log('form state', formState);
      // console.log('formState', formState)
      const firstErrorField = findFirstErrorField(errors);
      // console.log('firstErrorField', firstErrorField);
      const errorField =
        document.querySelector(`[name="${firstErrorField}"]`) ??
        document.getElementById(`${firstErrorField}`);
      console.log("errorField", errorField);
      if (errorField) {
        errorField.scrollIntoView({ behavior: "smooth" });
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  // useEffect(() => {
  //     if (blocker.state === "blocked") {
  //         dialogRef.current?.showModal(); // Show the dialog when blocked
  //     } else {
  //         dialogRef.current?.close(); // Hide the dialog if not blocked
  //     }
  // }, [blocker.state]);

  return (
    <div className={styles.rootContainer}>
      <Form
        initialValues={formData}
        mutators={{
          ...arrayMutators,
          setFieldTouched: setFieldTouchedMutator,
        }}
        onSubmit={(values) => {
          // console.log('values', values)
          return {};
        }}
        validate={(values) => {
          const { errors } = bankStatementValidation(
            values,
            underwritingFileJobs,
            statementAnalysisJobs
          );
          console.log("bank statement errors", errors);
          return errors;
        }}
        render={({ values, handleSubmit, form }) => {
          formRef.current = form;
          return (
            <form className={styles.formWrapper} onSubmit={handleSubmit}>
              <FormSpy
                subscription={{ valid: true, pristine: true, values: true }}
                onChange={(state) => {
                  // console.log('formState', state)
                  // formStepsValidityRef.current[UnderwritingSteps.company] = !state.valid;
                  setFormPristine(state.pristine);
                  setStepValidity(
                    UnderwritingSteps.documents as any,
                    state.valid
                  );
                  saveToLocalStorage?.(state.values);
                }}
              />
              <div id="rootBankError" className={styles.formRootContainer}>
                <div className={styles.sectionContainer}>
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>Bank Statement</div>
                    <div className={styles.sectionSubTitle}>
                      {/* Add statements for all your bank accounts with net-banking or upload PDFs. */}
                      Upload PDFs of all bank accounts that were active in the
                      last 12 months
                    </div>
                  </div>
                </div>
                <div className={styles.sectionFieldsContainer}>
                  <FieldArray name="bankStatements">
                    {({ fields }) => {
                      return (
                        <>
                          {fields.map((fieldName, fieldIndex) => {
                            return (
                              <>
                                <BankStatements
                                  deleteFileKeys={deleteFileKeys}
                                  setDeleteFileKeys={setDeleteFileKeys}
                                  fieldIndex={fieldIndex}
                                  fieldName={fieldName}
                                  deleteDocIds={deleteDocIds}
                                  setDeleteDocIds={setDeleteDocIds}
                                  newUploadedDocIds={newUploadedDocIds}
                                  setNewUploadedDocIds={setNewUploadedDocIds}
                                />
                                {fieldIndex != fields.length - 1 ? (
                                  <div className={styles.separator}></div>
                                ) : null}
                              </>
                            );
                          })}
                          <div className={styles.addBtnActionContainer}>
                            <div>
                              <MeritoButton
                                size="md2"
                                type="secondary-1"
                                icon={<AddIcon />}
                                btnText={"Add Another Account"}
                                onClick={() => {
                                  const currentLength = fields.length;
                                  const initialBankStatementDetails =
                                    getInitialBankStatementDetails();
                                  fields.push(initialBankStatementDetails);
                                  setTimeout(() => {
                                    onAddAccountScroll(
                                      `account_${currentLength}`
                                    );
                                  }, 100);
                                }}
                              />
                            </div>
                          </div>
                        </>
                      );
                    }}
                  </FieldArray>
                </div>
              </div>
            </form>
          );
        }}
      />
    </div>
  );
});

export default UploadDocuments;
